/**
 * Plain English tax guidance for Schedule E expense and equity categories.
 * Written at an 8th-grade reading level for non-accountant property owners.
 */

export const CATEGORY_GUIDANCE = {
  "Repairs": {
    title: "Repairs & Maintenance",
    scheduleELine: "Line 14 (Repairs)",
    irsRef: "IRC § 162; Treas. Reg. § 1.263(a)-3; IRS Pub 527",
    typicalPct: "100%",
    summary: "Ordinary work that keeps your rental in good working condition without adding significant new value or extending its useful life.",
    qualifies: [
      "Fixing a leaking pipe, running toilet, or garbage disposal",
      "Patching holes in drywall and touch-up repainting between tenants",
      "Replacing broken window panes, latches, or cabinet hinges",
      "Servicing or repairing an existing HVAC unit or water heater",
      "Replacing a few damaged roof shingles or clearing gutters",
    ],
    doesNotQualify: [
      "Replacing an entire roof (that is a Capital Improvement)",
      "Installing brand new central AC where none existed before",
      "Complete kitchen remodel or adding a new bathroom",
      "Major foundation replacement or structural additions",
    ],
    auditTips: "If the project costs more than $2,500 and replaces an entire system, the IRS expects you to capitalize and depreciate it over 27.5 years rather than deducting it all today.",
  },

  "Meals & Entertainment": {
    title: "Business Meals & Dining",
    scheduleELine: "Line 6 (Auto and Travel) or Line 19 (Other)",
    irsRef: "IRC § 274(k), § 274(n); IRS Pub 463",
    typicalPct: "50% (or 0%)",
    summary: "Meals directly related to active rental property business, such as meeting a tenant, contractor, or business partner.",
    qualifies: [
      "Lunch while meeting a contractor to inspect or plan repairs",
      "Coffee with a prospective long-term tenant during lease signing",
      "Meals during overnight travel solely for property management",
    ],
    doesNotQualify: [
      "Lavish or extravagant meals",
      "Solo meals while working at the rental on day trips (no overnight stay)",
      "Personal entertainment tickets (sports games, concerts, movies) — 0% deductible",
      "Family meals after showing an apartment",
    ],
    auditTips: "The IRS strictly limits business meals to 50% deductibility. You must keep receipts showing who attended, where, and the specific business purpose.",
  },

  "Auto & Travel": {
    title: "Auto, Mileage & Long-Distance Travel",
    scheduleELine: "Line 6 (Auto and Travel)",
    irsRef: "IRC § 162, § 274(d); IRS Pub 463; IRS Rev. Proc. 2024-40",
    typicalPct: "100% of business portion",
    summary: "Vehicle travel between properties, hardware stores, and tenant meetings, or trips to out-of-town properties.",
    qualifies: [
      "Driving from your home office to the rental property for inspections or repairs",
      "Trips to Home Depot, Lowe's, or supply stores for rental materials",
      "Driving to meet prospective tenants or sign lease documents",
      "Flights and hotel lodging for overnight out-of-town property management",
    ],
    doesNotQualify: [
      "Commuting from your regular W-2 job to the rental property",
      "Personal detours (errands, family visits) during a business trip",
      "Trips where primary purpose was vacation or leisure",
    ],
    auditTips: "IRS requires contemporaneous logs (date, destination, business purpose, and miles). Lapen Mileage Tracker automatically produces an IRS-compliant log.",
  },

  "Insurance": {
    title: "Property & Liability Insurance",
    scheduleELine: "Line 9 (Insurance)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100% (rental properties) or prorated",
    summary: "Premiums paid for hazard, fire, flood, theft, and liability protection on rental properties.",
    qualifies: [
      "Landlord dwelling policy and hazard insurance on 100% rental properties",
      "Umbrella liability policy dedicated to rental activities",
      "Flood and earthquake insurance for rental real estate",
      "Short-term rental commercial host liability insurance",
    ],
    doesNotQualify: [
      "Homeowners insurance on your personal residence (unless prorated for a rented room/ADU)",
      "Life insurance or personal auto insurance",
      "Multi-year policy premiums paid in advance (must be amortized over the policy years)",
    ],
    auditTips: "If you pay for a 3-year policy upfront, you can only deduct the portion applicable to the current tax year.",
  },

  "Utilities": {
    title: "Utilities",
    scheduleELine: "Line 17 (Utilities)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100% (rental units) or tenant-paid (0%)",
    summary: "Electricity, gas, water, sewer, trash, internet, and cable bills paid by the landlord.",
    qualifies: [
      "Electric, gas, water, and trash bills paid by the owner during tenant vacancy",
      "Utilities included in long-term rent that the owner pays directly",
      "High-speed Wi-Fi, streaming services, and utilities for Short-Term Rentals (STR)",
      "Common area lighting, security, and hallway power",
    ],
    doesNotQualify: [
      "Utility bills paid directly by tenants to the utility company",
      "Utilities for personal living spaces (unless home is duplex/ADU with documented square footage split)",
      "Reimbursed utilities where income was not recorded",
    ],
    auditTips: "If a tenant reimburses you for utilities, report the reimbursement as Income and the full utility bill as an Expense.",
  },

  "Legal & Professional": {
    title: "Legal & Professional Services",
    scheduleELine: "Line 10 (Legal and other professional fees)",
    irsRef: "IRC § 162, § 212; IRS Pub 527",
    typicalPct: "100% (routine operations) or capitalized (purchase/title)",
    summary: "Fees paid to lawyers, accountants, bookkeepers, tax preparers, and property consultants.",
    qualifies: [
      "CPA fees for preparing Schedule E or LLC tax filings",
      "Attorney fees to draft leases, resolve tenant disputes, or conduct evictions",
      "Monthly bookkeeping and property accounting software subscriptions",
    ],
    doesNotQualify: [
      "Legal fees to defend title or acquire a property (must be added to property basis/cost)",
      "Personal estate planning or personal tax preparation (Form 1040)",
      "Legal fees incurred to create a new LLC (amortized over 180 months under IRC § 195)",
    ],
    auditTips: "Legal fees related to buying a property cannot be expensed on Schedule E; they must be added to your purchase basis.",
  },

  "Mortgage Interest": {
    title: "Mortgage Interest",
    scheduleELine: "Line 12 (Mortgage interest paid to banks)",
    irsRef: "IRC § 163; IRS Pub 527, Pub 936; Form 1098",
    typicalPct: "100% (interest only; 0% principal)",
    summary: "Interest paid to financial institutions on loans used to purchase, build, or improve your rental.",
    qualifies: [
      "Mortgage interest reported on Box 1 of your bank's annual Form 1098",
      "Origination points amortized over the life of the loan",
      "Prepayment penalties paid on early payoff of a rental mortgage",
    ],
    doesNotQualify: [
      "Principal reduction payments (paying down your loan balance is NOT a tax deduction!)",
      "Escrow payments for property tax and insurance (deduct when actually paid to the county/insurer)",
      "Interest on cash-out refinances used for personal spending (vacations, personal cars)",
    ],
    auditTips: "Never enter your total monthly mortgage check as an expense! Only the interest portion is deductible; the principal portion is equity.",
  },

  "Other Interest": {
    title: "Other Business Interest",
    scheduleELine: "Line 13 (Other interest)",
    irsRef: "IRC § 163; IRS Pub 527",
    typicalPct: "100% of business portion",
    summary: "Interest paid on credit cards, lines of credit, or personal loans used exclusively for rental operations.",
    qualifies: [
      "Interest on a dedicated business credit card used for rental supplies",
      "Interest on a Home Equity Line of Credit (HELOC) spent directly on rental improvements",
      "Interest on private landlord loans or promissory notes",
    ],
    doesNotQualify: [
      "Interest on credit cards mixed with personal groceries or family purchases",
      "Interest on car loans not used for business",
      "Late payment fees charged by credit cards (classified under Other/Fees, not Interest)",
    ],
    auditTips: "Maintain separate business credit cards to prevent IRS interest tracing disputes.",
  },

  "Supplies": {
    title: "Supplies & Small Tools",
    scheduleELine: "Line 15 (Supplies)",
    irsRef: "IRC § 162; Treas. Reg. § 1.162-3; IRS Pub 527",
    typicalPct: "100%",
    summary: "Consumable items and small equipment used in the operation and maintenance of the rental.",
    qualifies: [
      "Smoke detectors, carbon monoxide alarms, fire extinguishers",
      "Key duplicates, lockboxes, doorstops, light bulbs, furnace filters",
      "Small hand tools under $2,500 (drills, screwdrivers, tape measures)",
      "Office supplies (printer ink, paper, postage) used for rental management",
    ],
    doesNotQualify: [
      "Major machinery or power equipment over $2,500 (must be depreciated)",
      "Items purchased for personal household use",
      "Building materials used in major renovations (part of capital improvement)",
    ],
    auditTips: "Under the IRS De Minimis Safe Harbor, supplies and tangible property costing under $2,500 per invoice can be fully expensed immediately.",
  },

  "Taxes": {
    title: "Real Estate & Local Taxes",
    scheduleELine: "Line 16 (Taxes)",
    irsRef: "IRC § 164; IRS Pub 527",
    typicalPct: "100%",
    summary: "State and local property taxes, rental license fees, and occupancy taxes paid for the property.",
    qualifies: [
      "County and city real estate property taxes paid during the tax year",
      "Annual city rental registration and landlord business license fees",
      "School district property taxes assessed against the rental property",
      "Local lodging or transient occupancy taxes paid out of pocket",
    ],
    doesNotQualify: [
      "Property taxes paid for personal residences (reported on Schedule A, not Schedule E)",
      "Special local assessments that increase property value (e.g., new city sidewalks, sewer mains — added to basis)",
      "Federal income taxes or IRS penalties",
    ],
    auditTips: "If your mortgage servicer escrows property taxes, only deduct the amount the bank actually paid to the county in this tax year, not your monthly escrow deposits.",
  },

  "Cleaning & Maintenance": {
    title: "Cleaning & Grounds Maintenance",
    scheduleELine: "Line 7 (Cleaning and maintenance)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100%",
    summary: "Routine services to keep the property clean, safe, and presentable.",
    qualifies: [
      "Turnover cleaning between tenants",
      "Lawn mowing, landscaping, leaf removal, tree trimming",
      "Snow and ice removal for tenant walkways and driveways",
      "Pest control and extermination contracts",
      "Pool servicing and chemical balancing",
    ],
    doesNotQualify: [
      "Landscaping overhauls that add permanent structures (retaining walls, new patios)",
      "Cleaning your own personal residence",
      "Your own sweat equity (you cannot pay yourself a deductible salary for cleaning)",
    ],
    auditTips: "Payments of $600 or more per year to unincorporated cleaning or lawn care contractors require issuing a Form 1099-NEC.",
  },

  "Management Fees": {
    title: "Property Management Fees",
    scheduleELine: "Line 11 (Management fees)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100%",
    summary: "Commissions and monthly retainers paid to third-party professional property management companies.",
    qualifies: [
      "Monthly property management fee (usually 7%–10% of gross rent)",
      "Tenant placement and lease-up fees charged by management companies",
      "Administrative setup and lease renewal fees paid to managers",
    ],
    doesNotQualify: [
      "Payments to yourself for managing your own rental (self-management is not deductible)",
      "Management fees paid on properties held strictly for personal use",
    ],
    auditTips: "Keep your property management monthly owner statements; they provide full backup for this deduction.",
  },

  "Advertising": {
    title: "Advertising & Marketing",
    scheduleELine: "Line 5 (Advertising)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100%",
    summary: "Costs spent to attract prospective tenants and market available vacancies.",
    qualifies: [
      "Online listings on Zillow, Apartments.com, Facebook Marketplace, Craigslist",
      "Yard signs, 'For Rent' banners, and lockbox banners",
      "Professional real estate photography or virtual 3D tours",
      "Local newspaper ads and rental flyer printing",
    ],
    doesNotQualify: [
      "Advertising for your primary W-2 business or unrelated ventures",
      "Gifts to prospective tenants exceeding IRS limits ($25 per individual)",
    ],
    auditTips: "Advertising is 100% deductible in the year incurred, even if the property wasn't leased until the following month.",
  },

  "Commissions": {
    title: "Real Estate Leasing Commissions",
    scheduleELine: "Line 8 (Commissions)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100%",
    summary: "Commissions paid to licensed real estate brokers and leasing agents to find and place tenants.",
    qualifies: [
      "Tenant placement commissions paid to real estate agents (e.g., half or full first month's rent)",
      "Finder's fees paid to licensed referral agents",
    ],
    doesNotQualify: [
      "Sales commissions paid when purchasing a property (added to property purchase basis)",
      "Sales commissions paid when selling a property (subtracted from sales proceeds on Form 4797)",
      "Referral fees paid to unlicensed tenants (review local state real estate commission laws)",
    ],
    auditTips: "Brokers paid $600 or more during the year must receive a Form 1099-MISC/NEC.",
  },

  "Consumables/Toiletries": {
    title: "STR Consumables & Guest Amenities",
    scheduleELine: "Line 19 (Other expenses — STR Amenities)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100%",
    summary: "Single-use supplies provided for Short-Term Rental guests.",
    qualifies: [
      "Shampoo, conditioner, body wash, soap bars, toilet paper, paper towels",
      "Coffee beans, tea bags, sugar packets, welcome snacks",
      "Dishwashing tablets, laundry detergent pods, trash bags",
    ],
    doesNotQualify: [
      "Personal toiletries used in your primary residence",
      "Permanent furniture, mattresses, or appliances (capital improvements / depreciation)",
    ],
    auditTips: "Track bulk purchases through warehouse clubs with itemized business receipts.",
  },

  "Professional Cleaning": {
    title: "Short-Term Rental Turnover Cleaning",
    scheduleELine: "Line 7 (Cleaning and maintenance)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100%",
    summary: "Cleaning fees paid between Airbnb, Vrbo, and direct short-term guests.",
    qualifies: [
      "Turnover cleaning between guest stays",
      "Linen washing and pressing services",
      "Deep cleaning and carpet steam cleaning between peak seasons",
    ],
    doesNotQualify: [
      "Housekeeping for personal stays at the property",
    ],
    auditTips: "Ensure independent cleaners provide invoices and Form W-9 if you pay them over $600/year.",
  },

  "Platform Fees": {
    title: "Booking & Credit Card Platform Fees",
    scheduleELine: "Line 19 (Other expenses — Platform & Merchant Fees)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "100%",
    summary: "Transaction fees deducted by STR platforms and merchant processors.",
    qualifies: [
      "Airbnb 3% host service fees",
      "Vrbo host commission and booking processing fees",
      "Stripe, Square, or PayPal payment processing fees for rent collection",
    ],
    doesNotQualify: [
      "Personal bank overdraft or account maintenance fees",
    ],
    auditTips: "If you record gross booking payouts, deduct the platform fees here. If you record net deposits, do not double-deduct!",
  },

  "Guest Supplies": {
    title: "Guest Linens & Kitchenware",
    scheduleELine: "Line 15 (Supplies) or Line 19 (Other)",
    irsRef: "IRC § 162; Treas. Reg. § 1.263(a)-1(f)",
    typicalPct: "100%",
    summary: "Non-permanent guest supplies under $2,500 that wear out quickly.",
    qualifies: [
      "Bedsheets, duvet covers, pillows, bath towels, pool towels",
      "Cookware sets, dishes, cutlery, glasses, cutting boards",
      "Board games, books, pool floaties, hair dryers",
    ],
    doesNotQualify: [
      "High-end furnishings or artwork over $2,500",
      "Items taken for personal home use",
    ],
    auditTips: "Take advantage of the $2,500 De Minimis Safe Harbor to expense quality replacement linens immediately.",
  },

  "Capital Contribution": {
    title: "Owner Capital Contribution",
    scheduleELine: "NOT on Schedule E (Balance Sheet / Equity)",
    irsRef: "IRC § 721; IRS Pub 541",
    typicalPct: "0% (Not an expense)",
    summary: "Money transferred from your personal bank account into the rental business or LLC.",
    qualifies: [
      "Transferring personal funds to cover a rental mortgage shortfall",
      "Initial cash deposit to open the LLC checking account",
      "Funding a major capital renovation out of personal savings",
    ],
    doesNotQualify: [
      "This is NEVER a tax-deductible expense on Schedule E! It increases your owner equity/basis.",
    ],
    auditTips: "Do not record this as rental income or an expense. It is an equity transfer on your balance sheet.",
  },

  "Owner Draw": {
    title: "Owner Draw / Member Distribution",
    scheduleELine: "NOT on Schedule E (Balance Sheet / Equity)",
    irsRef: "IRC § 731; IRS Pub 541",
    typicalPct: "0% (Not an expense)",
    summary: "Money transferred from the rental business account to your personal checking account for personal use.",
    qualifies: [
      "Withdrawing monthly rental profit to your personal account",
      "Owner distributions from LLC profit",
    ],
    doesNotQualify: [
      "This is NEVER a tax-deductible expense! You cannot expense payments to yourself.",
    ],
    auditTips: "You pay income tax on net rental profit regardless of whether you leave cash in the LLC or take an owner draw.",
  },

  "Due to Member": {
    title: "Due to Member / Owner Loan",
    scheduleELine: "NOT on Schedule E (Liability / Balance Sheet)",
    irsRef: "IRC § 707; IRS Pub 541",
    typicalPct: "0% (Not an expense)",
    summary: "Loans made between an LLC owner and the company that will be repaid.",
    qualifies: [
      "Reimbursing an owner who paid a rental contractor with a personal credit card",
      "A documented loan from an LLC member to the business",
    ],
    doesNotQualify: [
      "Loan repayments are not tax deductions. Only the interest portion of a formal loan is deductible.",
    ],
    auditTips: "Keep loan agreements written and dated to establish valid debt rather than equity capital.",
  },

  "Depreciation": {
    title: "Property & Asset Depreciation",
    scheduleELine: "Line 18 (Depreciation expense or depletion)",
    irsRef: "IRC § 167, § 168 (MACRS); Form 4562; IRS Pub 527, Pub 946",
    typicalPct: "Calculated via Form 4562",
    summary: "The annual tax deduction for wear and tear on your rental building and capital improvements over 27.5 years.",
    qualifies: [
      "Residential rental building value (excluding land) depreciated over 27.5 years (3.636% per year)",
      "Capital improvements (new roof, HVAC, addition) depreciated over 27.5 years",
      "Land improvements (driveways, fences, shrubs) depreciated over 15 years",
      "Appliances and carpet depreciated over 5 years (eligible for bonus depreciation)",
    ],
    doesNotQualify: [
      "Land value! Land never depreciates and must be subtracted from purchase price.",
      "Routine maintenance (which is expensed under Line 14 Repairs)",
    ],
    auditTips: "The IRS requires depreciation recapture tax when you sell, even if you forgot to claim depreciation on your return! Always claim it.",
  },

  "Other": {
    title: "Other Miscellaneous Expenses",
    scheduleELine: "Line 19 (Other)",
    irsRef: "IRC § 162; IRS Pub 527",
    typicalPct: "Varies (must have business justification)",
    summary: "Ordinary and necessary rental expenses that do not fit into any pre-printed IRS line.",
    qualifies: [
      "Bank service fees on dedicated rental checking accounts",
      "HOA dues and condominium maintenance assessments",
      "Tenant background and credit check service fees",
      "Storage unit rent for landlord tools and turnover supplies",
    ],
    doesNotQualify: [
      "Personal expenses or hobby costs",
      "Fines and penalties for city code or parking violations",
    ],
    auditTips: "Schedule E Line 19 requires an itemized statement listing each miscellaneous expense description and amount.",
  },
};

/**
 * Returns guidance for a given category name, or a sensible fallback if not found.
 */
export function getCategoryGuidance(categoryName) {
  if (!categoryName) return null;
  return CATEGORY_GUIDANCE[categoryName] || {
    title: categoryName,
    scheduleELine: "Line 19 (Other)",
    irsRef: "IRC § 162",
    typicalPct: "100% (if ordinary & necessary)",
    summary: "General rental operating expense.",
    qualifies: ["Direct expenses strictly incurred for rental operations."],
    doesNotQualify: ["Personal expenses or non-rental activities."],
    auditTips: "Maintain documentation and receipts to substantiate business purpose.",
  };
}
